import { Fragment, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { calcSelectedOptionIds } from "src/pages/configurator/calcSelectedOptionIds";
import {
  ConfiguratorStepper,
  ImageDisplay,
  ImageDisplayImageProps,
  ModalWrapper,
  StepWrapper,
} from "src/pages/configurator/components";
import {
  ComparePackageOptionsContent,
  ExpiredOptionsSwitch,
  NoOptions,
  OptionListContainer,
  OptionListItem,
} from "src/pages/configurator/components/options";
import { useConfiguratorContext } from "src/pages/configurator/Configurator.context";
import { AccordionOptionCard, DebugText, FlexibleOptionCard, InsideMapsIFrame, InsideMapsLocations } from "~components";
import { Css } from "~generated/css";
import { CheckoutConfigOptionGroupStep } from "~generated/graphqlTypes";
import { fail, stripNumbers, TestIds, useComputed, useTestIds } from "~utils";
import { UserWarning } from "../components";
import {
  ADDL_PERSONALIZATION_IMAGE_DISPLAY,
  getCCFGImages,
  getCutoffDaysRemaining,
  optionGroupFilter,
} from "../Configurator.utils";
import { InteriorStep_CheckoutOptionGroupFragment } from "./Interior.step.generated";

/**
 * Shows Interior Design Package (Spec Level, Interior Style) and Personalization options (Appliance Package, Sink SKUs).
 */
export function InteriorStep() {
  const { checkoutConfig } = useConfiguratorContext();
  // urlSubStep will be 1 for "packages", 2 for "Kitchen", 3 for "Living", etc.
  // urlRoomLocationId will be the room location id, or room name if we're in lakeside demo, from a qr code
  const { subStep: urlSubStep, roomLocationId: urlRoomLocationId } = useParams<{
    subStep?: string;
    roomLocationId?: string;
  }>();

  const subStep = useMemo(() => parseInt(urlSubStep ?? "1") ?? 1, [urlSubStep]);
  const tid = useTestIds({}, "interior-step");
  const currentPersonalizationStep = checkoutConfig?.interiorSubSteps.find(({ stepNumber }) => stepNumber === subStep);
  const filteredSubStepOptionGroups =
    checkoutConfig?.optionGroups.filter(
      (ccog) =>
        // Show all options for the current personalization step, except options with parents
        // Those options will render with their prereq internally in ProductAddOnCard
        ccog.interiorSubStep?.stepNumber === currentPersonalizationStep?.stepNumber && !ccog.parentGroup,
    ) ?? [];

  const { location: readyPlanLocation, displayName = "" } = currentPersonalizationStep ?? {};

  const insideMapImages = getCCFGImages(checkoutConfig, true);

  const initialRoomIndex = useMemo(() => {
    if (urlRoomLocationId) {
      // Is this is a real inside map setup room location?
      if (urlRoomLocationId.startsWith("l:")) {
        const imageIndex = insideMapImages.findIndex(
          (image) => image.kind === "insideMaps" && image?.roomLocationId === urlRoomLocationId,
        );
        return imageIndex !== -1 ? imageIndex : 0;
      }
    }
    return 0;
  }, [insideMapImages, urlRoomLocationId]);

  return subStep === 1 ? (
    <PackageSubStep tid={tid} insideMapImages={insideMapImages} initialRoomIndex={initialRoomIndex} />
  ) : (
    <PersonalizationSubStep
      tid={tid}
      // The substep name is only expected to be undefined here when loading the initial context state
      // (user was linked directly to a room or refreshed the page)
      subStepId={currentPersonalizationStep?.id ?? ""}
      subStep={displayName}
      subStepGroups={filteredSubStepOptionGroups}
      roomLocationId={readyPlanLocation?.location.id}
    />
  );
}

type PackageSubStepProps = {
  tid: TestIds;
  initialRoomIndex: number;
  insideMapImages: ImageDisplayImageProps[];
};

// TODO: We need to add the option image on select, even if a child option, onto the left. I think for all steps not just here
/** Show 'choose your Design Package' spec level/style. */
function PackageSubStep(props: PackageSubStepProps) {
  const { tid, initialRoomIndex, insideMapImages } = props;
  const { toggleOption, checkoutConfig, formState, showPriceLoadingState, autoSaving, selectedOptionIds } =
    useConfiguratorContext();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Spec Level, Interior Style, etc
  const dpOptions = (checkoutConfig?.optionGroups ?? [])
    .filter((ccog) => ccog.step === CheckoutConfigOptionGroupStep.InteriorDesignPackage)
    // If we're looking at spec level, and we "truly" only have one spec level option available regardless of active or not,
    // then don't even bother showing this group
    .filter((ccog) => {
      if (ccog.group.group.type.isSpecLevel) {
        return ccog.options.length > 1;
      }
      return true;
    });

  const isDisabled = !checkoutConfig?.canEdit.allowed;
  const specComparisonUrl = checkoutConfig?.community.packageComparison?.downloadUrl;
  const loading = useComputed(() => formState.loading, [formState]);

  useEffect(() => {
    if (initialRoomIndex !== 0) {
      setSelectedImageIndex(initialRoomIndex);
    }
  }, [initialRoomIndex]);

  return (
    <>
      <StepWrapper
        loading={loading}
        loadingView="accordion"
        title="Interior Package"
        description="Select your design scheme and finish level. You will be able to swap individual finishes later."
        warningNode={
          !loading &&
          isDisabled && (
            <UserWarning
              title="Interior package can no longer be changed."
              disabledReasons={[checkoutConfig?.canEdit]}
            />
          )
        }
        showMaxExpand
        left={
          <ImageDisplay
            images={insideMapImages}
            imageIndex={selectedImageIndex}
            onClick={(index) => setSelectedImageIndex(index)}
            {...tid.imageDisplay}
          />
        }
        right={
          <>
            {dpOptions.map((ccog) => {
              return (
                <AccordionOptionCard
                  key={ccog.group.id}
                  title={
                    <>
                      <DebugText>{ccog.group.id}</DebugText>
                      {ccog.group.name}
                    </>
                  }
                  cutoffDate={ccog.cutoffDate}
                  titleAction={
                    // Only show compare button for `Spec Level`
                    !ccog.group.group.type.isInteriorScheme && !!specComparisonUrl
                      ? {
                          triggerText: "Compare",
                          onClick: () => {
                            window.open(specComparisonUrl, "_blank");
                          },
                          size: "sm",
                        }
                      : undefined
                  }
                  children={
                    <OptionListContainer>
                      {ccog.options
                        // Only show allowed spec levels for the selected interior scheme
                        .filter((cco) => cco.isEnabled)
                        .map((cco) => {
                          const { id, name, description } = cco.option;

                          const { removedOptions } = calcSelectedOptionIds(
                            checkoutConfig?.optionGroups ?? [],
                            selectedOptionIds,
                            ccog,
                            cco,
                          );
                          const isSelected = selectedOptionIds.includes(id);

                          return (
                            <Fragment key={id}>
                              <DebugText>{id}</DebugText>
                              <OptionListItem
                                isRadio
                                autoSaving={showPriceLoadingState(id)}
                                optionId={id}
                                conflicts={removedOptions}
                                title={name}
                                description={description}
                                selected={isSelected}
                                onClick={() => toggleOption(ccog, cco)}
                                priceInCents={cco.totalPriceInCents}
                                disabled={isDisabled || autoSaving}
                                {...tid.optionItem}
                              />
                            </Fragment>
                          );
                        })}
                    </OptionListContainer>
                  }
                  {...tid.accordionOptionCard}
                />
              );
            })}
          </>
        }
      />
      <ConfiguratorStepper />
    </>
  );
}

type PersonalizationSubStepProps = {
  subStepId: string;
  subStep: string; // "Whole House" | "Kitchen" | "Living" | etc
  subStepGroups: InteriorStep_CheckoutOptionGroupFragment[];
  tid: TestIds;
  roomLocationId: string | undefined;
};

/** Show personalization product/package options. */
function PersonalizationSubStep(props: PersonalizationSubStepProps) {
  const { subStepId, subStep, subStepGroups, tid, roomLocationId } = props;
  const { toggleOption, formState, checkoutConfig, selectedOptionIds } = useConfiguratorContext();
  const [showExpiredOptions, setShowExpiredOptions] = useState(false);
  // Tell IFrame to hide previews when summary drawer opens
  const [hidePreviews, setHidePreviews] = useState(false);

  const someOptionsHaveExpired = useMemo(() => {
    return subStepGroups.some((ccog) => ccog.cutoffDate && getCutoffDaysRemaining(ccog.cutoffDate) <= 0);
  }, [subStepGroups]);

  const { filteredSubStepGroups } = useMemo(
    () => ({
      // Hide any personalization options that are not enabled in addition to any expired.
      filteredSubStepGroups: subStepGroups.filter(optionGroupFilter(showExpiredOptions, selectedOptionIds)),
    }),
    [subStepGroups, showExpiredOptions, selectedOptionIds],
  );

  const [compareCCOG, setCompareCCOG] = useState<InteriorStep_CheckoutOptionGroupFragment | undefined>(undefined);
  const isDisabled = !checkoutConfig?.canEdit.allowed;
  const loading = useComputed(() => formState.loading, [formState]);

  const isAdditionalPersonalization = subStepId === "additional-personalization";
  return (
    <>
      <StepWrapper
        loading={loading}
        loadingView="personalization"
        title={stripNumbers(subStep)}
        description={
          <div css={Css.df.fdc.gap1.$}>
            {!loading && isDisabled && (
              <UserWarning
                title={`${subStep} options can no longer be changed.`}
                disabledReasons={[checkoutConfig?.canEdit]}
              />
            )}
          </div>
        }
        hideBorder
        hideTopOnMobile={isAdditionalPersonalization}
        showMaxExpand={!isAdditionalPersonalization}
        left={
          <div css={Css.h100.w100.df.fdc.$}>
            {/* Additional Personalization does not have an iframe */}
            {isAdditionalPersonalization ? (
              <ImageDisplay images={ADDL_PERSONALIZATION_IMAGE_DISPLAY} {...tid.imageDisplay} />
            ) : (
              <InsideMapsIFrame
                // Hide alternate camera toggle if we're showing a room w/ an alternative camera for whole house (ie Kitchen)
                hideAlternateCameraToggle={subStepId === "whole-house"}
                roomLocationId={roomLocationId}
                isPreview={false}
                iframeId={subStep}
                hidePreviews={hidePreviews}
              />
            )}
            <InsideMapsLocations hidePreviews={hidePreviews} />
          </div>
        }
        right={
          <>
            {someOptionsHaveExpired && (
              <ExpiredOptionsSwitch
                showExpiredOptions={showExpiredOptions}
                setShowExpiredOptions={setShowExpiredOptions}
              />
            )}
            <OptionListContainer>
              {filteredSubStepGroups.isEmpty && <NoOptions showExpiredOptions={showExpiredOptions} />}
              {filteredSubStepGroups.map((ccog) => {
                return (
                  <FlexibleOptionCard
                    key={ccog.id}
                    ccfgOptionGroup={ccog}
                    onCompare={(maybeChildCCOG) => setCompareCCOG(maybeChildCCOG ?? ccog)}
                    {...tid.flexibleOptionCard}
                  />
                );
              })}
            </OptionListContainer>
          </>
        }
      />
      <ConfiguratorStepper
        onSummaryDrawerToggle={(isOpen) => {
          // Our summary drawer ALSO displays inside maps iFrames. We need to derender them to prevent webGL context issues and prevent
          // possibly breaking the iframes for an entire session. The problem is that the summary drawer CLOSE animation becomes janky when we don't
          // delay re-rendering this main steps InsideMapsLocations previews which is why we have a hacky timeout here. 300ms is the default framer-motion
          // animation duration the drawer uses. Setting it to 305ms keeps the animation smooth and prevents the jankyness.
          setTimeout(
            () => {
              setHidePreviews(isOpen);
            },
            isOpen ? 0 : 305,
          );
        }}
      />
      <ModalWrapper isOpen={!!compareCCOG} title={compareCCOG?.group.name} close={() => setCompareCCOG(undefined)}>
        {compareCCOG && checkoutConfig && (
          <ComparePackageOptionsContent
            checkoutConfigId={checkoutConfig.id}
            rpogId={compareCCOG.group.id}
            ccog={compareCCOG}
            selectedOptionIds={selectedOptionIds}
            onSelect={(selectedOptionId) =>
              toggleOption(
                compareCCOG,
                compareCCOG.options.find((cco) => cco.id === selectedOptionId) ?? fail(`Missing ${selectedOptionId}`),
              )
            }
          />
        )}
      </ModalWrapper>
    </>
  );
}
